export const RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND';
export const RESOURCE_FETCH_FAILED = 'RESOURCE_FETCH_FAILED';

export type ResourceErrorCode =
    | typeof RESOURCE_NOT_FOUND
    | typeof RESOURCE_FETCH_FAILED;

export type ResourceError = {
    code: ResourceErrorCode;
    status?: number;
    cause?: any;
};

export const getErrorStatus = (error: any): number | undefined => {
    const status = error?.response?.status ?? error?.status;
    return typeof status === 'number' ? status : undefined;
};

export const isNotFoundError = (error: any): boolean =>
    error?.code === RESOURCE_NOT_FOUND
    || error?.code === 'ORDER_NOT_FOUND'
    || getErrorStatus(error) === 404;

export const isRetryableError = (error: any): boolean => {
    const status = getErrorStatus(error);
    return !status || status === 429 || status >= 500;
};

const RATE_LIMITED = 429;
const MAX_RATE_LIMIT_WAIT_MS = 12000;

/**
 * The catalogue APIs advertise their throttle via `ratelimit-reset` / `retry-after` (both in
 * seconds). Retrying a 429 on a short fixed backoff just burns more of the same budget, so wait
 * out the advertised window instead.
 */
const getRateLimitWaitMs = (error: any): number | null => {
    if (getErrorStatus(error) !== RATE_LIMITED) return null;

    const headers = error?.response?.headers || {};
    const advertised = headers['retry-after'] ?? headers['ratelimit-reset'];
    const seconds = Number(advertised);
    const waitMs = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 10000;

    return Math.min(waitMs, MAX_RATE_LIMIT_WAIT_MS);
};

export const createNotFoundError = (
    status?: number,
    cause?: any
): ResourceError => ({
    code: RESOURCE_NOT_FOUND,
    status,
    cause
});

export const createFetchError = (
    cause: any,
    status = getErrorStatus(cause)
): ResourceError => ({
    code: RESOURCE_FETCH_FAILED,
    status,
    cause
});

export const withRetry = async <T>(
    request: () => Promise<T>,
    delaysMs: number[] = [600, 1800]
): Promise<T> => {
    let attempt = 0;

    while (true) {
        try {
            return await request();
        } catch (error) {
            if (attempt >= delaysMs.length || !isRetryableError(error)) {
                throw error;
            }
            const waitMs = getRateLimitWaitMs(error) ?? delaysMs[attempt];
            await new Promise((resolve) => setTimeout(resolve, waitMs));
            attempt += 1;
        }
    }
};

export const normalizeResourceError = (error: any): ResourceError => {
    if (isNotFoundError(error)) {
        return createNotFoundError(getErrorStatus(error), error);
    }
    if (error?.code === RESOURCE_FETCH_FAILED) return error;
    return createFetchError(error);
};
